import { Dispatch, MouseEvent, SetStateAction, useCallback, useEffect } from 'react';
import { HStack, useToast, Button, Text } from '@cad-arc';
import { EventPanelFormType } from '@mark43/cad-api';
import { useFormState } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios-cad-nx';
import { countDirtyFields } from '../event-panel/count-dirty-fields';
import {
    FR_CREATE_EVENT_SUB_PATH,
    FR_EVENT_DETAILS_SUB_PATH,
    FR_EVENT_ROOT_PATH,
    ROOT_PATH,
} from '../router/constants';
import { FrPageFooter } from '../fr-page-layout/fr-page-footer';
import { useAddVehicleToForm, useAddPersonToForm } from './hooks';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { useSubmitEventForm } from '../common/hooks/use-submit-event-form';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ResponsiveButtons } from '../common/components/responsive-buttons';
import { buttonBreakpoints } from './constants';
import { useOpenFrEventDetails } from '../fr-event-details-page/hooks/use-open-fr-event-details';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';

export function FrCreateOrEditEventFooter({
    setUserManuallyAddedVehicleField,
    setUserManuallyAddedPersonField,
}: {
    setUserManuallyAddedVehicleField: Dispatch<SetStateAction<boolean>>;
    setUserManuallyAddedPersonField: Dispatch<SetStateAction<boolean>>;
}) {
    const navigate = useNavigate();
    const { form, isSubmitting } = useIntegratedForm<EventPanelFormType>();
    const addVehicleToForm = useAddVehicleToForm();
    const addPersonToForm = useAddPersonToForm();
    const submitEventForm = useSubmitEventForm();
    const openFrEventDetails = useOpenFrEventDetails();
    const location = useLocation();
    const unitAssignment = useUnitAssignment();
    const isCreateEventForm = location.pathname.includes(
        `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}`
    );
    const toast = useToast();
    const { rollOutFrFooterAndTabRedesignEnabled } = useEnabledFeatures();
    const { dirtyFields } = useFormState({ control: form.control });
    const dirtyFieldCount = countDirtyFields(dirtyFields);

    const handleAddTowVehicle = useCallback(() => {
        addVehicleToForm('towVehicles');
        setUserManuallyAddedVehicleField(true);
    }, [addVehicleToForm, setUserManuallyAddedVehicleField]);

    const handleAddVehicle = useCallback(() => {
        addVehicleToForm('vehicles');
        setUserManuallyAddedVehicleField(true);
    }, [addVehicleToForm, setUserManuallyAddedVehicleField]);

    const handleAddInvolvedPerson = useCallback(() => {
        addPersonToForm('involvedPersons');
        setUserManuallyAddedPersonField(true);
    }, [addPersonToForm, setUserManuallyAddedPersonField]);

    const handleAddReportingParty = useCallback(() => {
        addPersonToForm('reportingParties');
        setUserManuallyAddedPersonField(true);
    }, [addPersonToForm, setUserManuallyAddedPersonField]);

    useEffect(
        () => {
            if (location?.state?.shouldBeSaved) {
                saveEvent();
                navigate(location.pathname, { replace: true }); // This clears the state but stays on the same page
            }
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [location?.state?.shouldBeSaved]
    );

    const onCancel = useCallback(() => {
        form.reset();

        if (isCreateEventForm) {
            navigate(ROOT_PATH);
        } else if (location.key === 'default') {
            // if the edit page was loaded directly, there is no history, so go back to the event
            openFrEventDetails(
                form.getValues('eventId') ?? undefined,
                FR_EVENT_DETAILS_TABS.NARRATIVE
            );
        } else {
            navigate(-1);
        }
    }, [navigate, form, isCreateEventForm, location.key, openFrEventDetails]);

    const saveEvent = (clickEvent?: MouseEvent<HTMLButtonElement>) => {
        if (isCreateEventForm && unitAssignment?.unitId) {
            form.setValue('selfInitiateUnitId', unitAssignment.unitId);
        }

        const eventId = form.getValues('eventId');
        const targetPath =
            !rollOutFrFooterAndTabRedesignEnabled || isCreateEventForm
                ? ROOT_PATH
                : `${FR_EVENT_ROOT_PATH}/${eventId}/${FR_EVENT_DETAILS_SUB_PATH}`;

        submitEventForm(
            clickEvent,
            () => {
                form.reset();
                navigate(targetPath, {
                    state: {
                        previousLocationPathname: location.pathname,
                    },
                    replace: true,
                });
            },
            (error) => {
                if (error instanceof AxiosError && error?.response?.data?.error) {
                    toast({
                        description: error.response.data.error,
                        status: 'error',
                    });
                }
                if (location?.state?.shouldBeSaved) {
                    navigate(location.pathname, { replace: true }); // This clears the state but stays on the same page
                }
            }
        );
    };

    return (
        <FrPageFooter>
            <HStack
                margin={0}
                spacing={4}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
            >
                <FormCancelButton size="xl" onClick={onCancel} isDisabled={isSubmitting}>
                    Cancel
                </FormCancelButton>
                {rollOutFrFooterAndTabRedesignEnabled && dirtyFieldCount > 0 && (
                    <Text variant="caption" color="tertiary" style={{ whiteSpace: 'nowrap' }}>
                        {dirtyFieldCount} unsaved change{dirtyFieldCount !== 1 ? 's' : ''}
                    </Text>
                )}
                <ResponsiveButtons
                    moreActionsButtonProps={{ size: 'xl' }}
                    overflowVariant={rollOutFrFooterAndTabRedesignEnabled ? 'menu' : 'popover'}
                    containerProps={{
                        margin: 0,
                        marginLeft: 'auto',
                        padding: 0,
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                    buttons={[
                        {
                            breakpoint: buttonBreakpoints.ADD_TOW_VEHICLE,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={handleAddTowVehicle}
                                >
                                    Tow Vehicle
                                </Button>
                            ),
                            menuItemLabel: 'Add Tow Vehicle',
                            menuItemOnSelect: handleAddTowVehicle,
                            menuItemDisabled: isSubmitting,
                        },
                        {
                            breakpoint: buttonBreakpoints.ADD_VEHICLE,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={handleAddVehicle}
                                >
                                    Vehicle
                                </Button>
                            ),
                            menuItemLabel: 'Add Vehicle',
                            menuItemOnSelect: handleAddVehicle,
                            menuItemDisabled: isSubmitting,
                        },
                        {
                            breakpoint: buttonBreakpoints.ADD_INVOLVED_PERSON,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={handleAddInvolvedPerson}
                                >
                                    Involved Person
                                </Button>
                            ),
                            menuItemLabel: 'Add Involved Person',
                            menuItemOnSelect: handleAddInvolvedPerson,
                            menuItemDisabled: isSubmitting,
                        },
                        {
                            breakpoint: buttonBreakpoints.ADD_REPORTING_PARTY,
                            component: (
                                <Button
                                    size="xl"
                                    leadingVisual="Add"
                                    isDisabled={isSubmitting}
                                    onClick={handleAddReportingParty}
                                >
                                    Reporting Party
                                </Button>
                            ),
                            menuItemLabel: 'Add Reporting Party',
                            menuItemOnSelect: handleAddReportingParty,
                            menuItemDisabled: isSubmitting,
                        },
                    ]}
                />
                <FormSaveButton size="xl" onClick={saveEvent}>
                    Save
                </FormSaveButton>
            </HStack>
        </FrPageFooter>
    );
}
